import type { FilhoEtapa2Candidato } from '../../../models';
import {
  ArquivosEtapa2,
  ARQUIVOS_FILHO_INICIAIS,
  ArquivosFilhoEtapa2,
  CASADO_OU_UNIAO_ESTAVEL,
  LABEL_CLASS,
} from '../candidatoFormExternoEtapa2Page.definitions';
import CampoArquivo from './CampoArquivo';

export type ModoDocumentoIdentidade = 'frente' | 'verso' | 'frente_e_verso_juntos' | null;

interface EtapaDocumentosProps {
  estadoCivil: string | null;
  possuiDeficiencia: boolean;
  filhos: FilhoEtapa2Candidato[];
  arquivos: ArquivosEtapa2;
  arquivosFilhos: ArquivosFilhoEtapa2[];
  modoIdentidade: ModoDocumentoIdentidade;
  onChangeModoIdentidade: (modo: ModoDocumentoIdentidade) => void;
  onChangeArquivo: (campo: keyof ArquivosEtapa2, arquivo: File | null) => void;
  onChangeArquivoFilho: (index: number, campo: keyof ArquivosFilhoEtapa2, arquivo: File | null) => void;
}

const OPCOES_MODO_IDENTIDADE: Array<{ valor: Exclude<ModoDocumentoIdentidade, null>; texto: string }> = [
  { valor: 'frente_e_verso_juntos', texto: 'Tenho um arquivo só, com frente e verso juntos' },
  { valor: 'frente', texto: 'Só tenho a frente' },
  { valor: 'verso', texto: 'Tenho frente e verso em arquivos separados' },
];

export default function EtapaDocumentos({
  estadoCivil,
  possuiDeficiencia,
  filhos,
  arquivos,
  arquivosFilhos,
  modoIdentidade,
  onChangeModoIdentidade,
  onChangeArquivo,
  onChangeArquivoFilho,
}: EtapaDocumentosProps) {
  return (
    <>
      <h2 className="mb-2 text-2xl font-bold text-gray-800">Documentos</h2>
      <p className="mb-6 text-sm text-gray-600">
        Se puder, já envie os documentos abaixo — isso agiliza bastante o seu cadastro. Se não tiver em mãos agora,
        pode continuar sem problema, o RH entra em contato depois.
      </p>
      <div className="space-y-5">
        <div>
          <label className={LABEL_CLASS}>Como você vai enviar seu documento de identificação (RG/CNH)?</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            {OPCOES_MODO_IDENTIDADE.map((opcao) => (
              <button
                key={opcao.valor}
                type="button"
                onClick={() => onChangeModoIdentidade(opcao.valor)}
                className={`flex-1 rounded-xl border-2 p-3 text-sm font-medium transition-colors ${
                  modoIdentidade === opcao.valor
                    ? 'border-indigo-600 bg-indigo-50/50 text-indigo-700'
                    : 'border-gray-100 text-gray-600 hover:border-indigo-200'
                }`}
              >
                {opcao.texto}
              </button>
            ))}
          </div>
        </div>

        {modoIdentidade === 'frente_e_verso_juntos' && (
          <CampoArquivo
            label="Documento de Identificação (frente e verso juntos)"
            arquivo={arquivos.identidadeFrente}
            onChange={(a) => onChangeArquivo('identidadeFrente', a)}
          />
        )}
        {modoIdentidade === 'frente' && (
          <CampoArquivo
            label="Documento de Identificação (frente)"
            arquivo={arquivos.identidadeFrente}
            onChange={(a) => onChangeArquivo('identidadeFrente', a)}
          />
        )}
        {modoIdentidade === 'verso' && (
          <>
            <CampoArquivo
              label="Documento de Identificação (frente)"
              arquivo={arquivos.identidadeFrente}
              onChange={(a) => onChangeArquivo('identidadeFrente', a)}
            />
            <CampoArquivo
              label="Documento de Identificação (verso)"
              arquivo={arquivos.identidadeVerso}
              onChange={(a) => onChangeArquivo('identidadeVerso', a)}
            />
          </>
        )}

        <CampoArquivo
          label="Comprovante de Endereço"
          arquivo={arquivos.comprovanteEndereco}
          onChange={(a) => onChangeArquivo('comprovanteEndereco', a)}
        />
        <CampoArquivo
          label="Foto recente sua"
          arquivo={arquivos.fotoRecente}
          onChange={(a) => onChangeArquivo('fotoRecente', a)}
        />

        {estadoCivil && CASADO_OU_UNIAO_ESTAVEL.includes(estadoCivil) && (
          <>
            <CampoArquivo
              label="Certidão de Casamento/União Estável"
              arquivo={arquivos.certidaoCasamento}
              onChange={(a) => onChangeArquivo('certidaoCasamento', a)}
            />
            <CampoArquivo
              label="RG/Identidade do cônjuge"
              arquivo={arquivos.rgConjuge}
              onChange={(a) => onChangeArquivo('rgConjuge', a)}
            />
          </>
        )}

        {/* Opcionais — mesmos documentos que o perfil tem em "Documentos da família". */}
        <CampoArquivo
          label="RG/Identidade da mãe (opcional)"
          arquivo={arquivos.rgMae}
          onChange={(a) => onChangeArquivo('rgMae', a)}
        />
        <CampoArquivo
          label="RG/Identidade do pai (opcional)"
          arquivo={arquivos.rgPai}
          onChange={(a) => onChangeArquivo('rgPai', a)}
        />
        {possuiDeficiencia && (
          <CampoArquivo
            label="Laudo médico PCD (opcional)"
            arquivo={arquivos.laudoPcd}
            onChange={(a) => onChangeArquivo('laudoPcd', a)}
          />
        )}

        {filhos.map((filho, index) => {
          const docsFilho = arquivosFilhos[index] ?? ARQUIVOS_FILHO_INICIAIS;
          return (
            <div key={index} className="space-y-3 rounded-xl border-2 border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-gray-700">Documentos de {filho.nome || `filho(a) ${index + 1}`}</h3>
              <CampoArquivo
                label="Certidão de nascimento (opcional)"
                arquivo={docsFilho.certidaoNascimento}
                onChange={(a) => onChangeArquivoFilho(index, 'certidaoNascimento', a)}
              />
              <CampoArquivo
                label="RG/Identidade (opcional)"
                arquivo={docsFilho.rgIdentidade}
                onChange={(a) => onChangeArquivoFilho(index, 'rgIdentidade', a)}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}
